import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UserAccount } from '@app/models/account.model';
import { UserService } from '@app/services/admin/user.service';
import { ToastService } from '@app/services/toast.service';
import { ButtonComponent } from '@app/ui-components/button/button.component';
import { DetailsPageHeaderComponent } from '@app/ui-components/details-page-header/details-page-header.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { finalize, Observable, switchMap } from 'rxjs';
import { DetailsTabComponent } from './details-tab/details-tab.component';
import { EntitiesTabComponent } from './entities-tab/entities-tab.component';
import { QualificationTabComponent } from './qualification-tab/qualification-tab.component';

type Tab = 'details' | 'entities' | 'qualification';
interface TabDef {
  key: Tab;
  label: string;
}

@Component({
  selector: 'app-user-details',
  standalone: true,
  templateUrl: './user-details.component.html',
  styleUrl: './user-details.component.scss',
  imports: [
    CommonModule,
    DetailsTabComponent,
    EntitiesTabComponent,
    QualificationTabComponent,
    TranslatePipe,
    DetailsPageHeaderComponent,
    ButtonComponent,
  ],
})
export class UserDetailsComponent implements OnInit {
  userId: string | null = null;
  user: UserAccount | null = null;
  personalData = signal<UserAccount | null>(null);

  isLoading = true;
  isSaving = false;
  isCancelling = false;
  isToggling = false;
  isSkipping = false;
  isResetting = false;
  isDeleting = false;

  activeTab: Tab = 'details';
  tabs: ReadonlyArray<TabDef> = [
    { key: 'details', label: 'admin.users.tabs.details' },
    { key: 'entities', label: 'admin.users.tabs.entities' },
    { key: 'qualification', label: 'admin.users.tabs.qualification' },
  ];

  @ViewChild(DetailsTabComponent) detailsTab?: DetailsTabComponent;
  @ViewChild('copyToast') copyToastEl?: ElementRef<HTMLDivElement>;

  constructor(
    private route: ActivatedRoute,
    private userService: UserService,
    private router: Router,
    private toast: ToastService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.userId = this.route.snapshot.paramMap.get('id');
    if (this.userId) this.loadUser();
  }
  private loadUser(): void {
    this.isLoading = true;
    this.userService
      .getUserById(this.userId!)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (user: UserAccount) => {
          if (user.deleted === true) {
            this.router.navigate(['/admin/users']);
            return;
          }
          this.user = user;
          this.personalData.set(user);
        },
        error: () => {},
      });
  }

  get isDeactivated(): boolean {
    return !!this.personalData()?.deactivated;
  }

  get verificationIcon(): string {
    if (this.isSkipping) return 'fas fa-spinner fa-spin';
    return this.user?.verified !== true ? 'fas fa-lock' : 'fas fa-lock-open';
  }
  get canSkip(): boolean {
    return (
      !!this.userId &&
      !this.isSkipping &&
      !this.isLoading &&
      this.personalData()?.verified !== true
    );
  }
  get isDeleted(): boolean {
    return this.personalData()?.deactivated === true;
  }

  onToggleActivation(): void {
    if (!this.userId || this.isToggling) return;
    this.isToggling = true;

    const action: 'reactivate' | 'deactivate' = this.isDeactivated
      ? 'reactivate'
      : 'deactivate';

    const request: Observable<void> =
      action === 'reactivate'
        ? this.userService.reactivateUser(this.userId)
        : this.userService.deactivateUser(this.userId);

    request
      .pipe(
        switchMap(() => this.userService.getUserById(this.userId!)),
        finalize(() => (this.isToggling = false))
      )
      .subscribe({
        next: (user) => {
          this.personalData.set(user);
          const okKey =
            action === 'reactivate'
              ? 'admin.toasts.userReactivated'
              : 'admin.toasts.userSuspended';
          this.toast.success(this.translate.instant(okKey));
        },
        error: () => {
          const errKey =
            action === 'reactivate'
              ? 'admin.toasts.userReactivateFailed'
              : 'admin.toasts.userSuspendFailed';
          this.toast.error(this.translate.instant(errKey));
        },
      });
  }

  onSkipPersonalVerification(): void {
    if (!this.userId || this.isSkipping) return;

    this.isSkipping = true;

    this.userService
      .skipPersonalVerification(this.userId)
      .pipe(
        switchMap(() => this.userService.getUserById(this.userId!)),
        finalize(() => (this.isSkipping = false))
      )
      .subscribe({
        next: (user) => {
          this.user = user;
          this.personalData.set(user);
          this.toast.success(
            this.translate.instant('admin.toasts.skipVerificationSuccess')
          );
        },
        error: (error) => {
          this.toast.error(
            this.translate.instant('admin.toasts.skipVerificationFailed')
          );
          console.error('Error:', error);
        },
      });
  }
  onResetPassword(): void {
    const email = this.personalData()?.email?.trim();
    if (!email || this.isResetting) return;
    this.isResetting = true;

    this.userService
      .resetPassword(this.userId!)
      .pipe(finalize(() => (this.isResetting = false)))
      .subscribe({
        next: () =>
          this.toast.success(
            this.translate.instant('admin.toasts.resetPasswordSent')
          ),
        error: () =>
          this.toast.error(
            this.translate.instant('admin.toasts.resetPasswordFailed')
          ),
      });
  }

  onDelete(): void {
    if (!this.userId || this.isDeleting) return;

    this.isDeleting = true;
    this.userService
      .deleteUser(this.userId)
      .pipe(finalize(() => (this.isDeleting = false)))
      .subscribe({
        next: () => {
          this.toast.success(
            this.translate.instant('admin.toasts.deletedSuccess')
          );
          this.router.navigate(['/admin/users']);
        },
        error: () => {
          this.toast.error(this.translate.instant('admin.toasts.deleteFailed'));
        },
      });
  }

  goBack(): void {
    const target = history.state?.from || '/admin/users';
    this.router.navigate([target], { relativeTo: this.route });
  }
  setTab(tab: Tab): void {
    this.activeTab = tab;
  }
  isDetailsActive(): boolean {
    return (
      document.getElementById('details')?.classList.contains('active') ?? false
    );
  }

  onSaveDetails(): void {
    const save$ = this.detailsTab?.save();
    if (!save$) return;

    this.isSaving = true;
    save$.pipe(finalize(() => (this.isSaving = false))).subscribe({
      next: () => this.loadUser(),
      error: (error) => console.error('Save failed', error),
    });
  }

  onCancelDetails(): void {
    this.isCancelling = true;
    this.detailsTab?.cancel();
    this.isCancelling = false;
  }
}
